import React from 'react';
import PropTypes from 'prop-types';
import {
	Button,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	Typography,
} from '@material-ui/core';
import preinformeStyles from '../preinforme/preinformeStyles';
import medicionesStyles from './medicionesStyles';
import {
	HISTORIAL_OPCIONES_TAMANO,
	MENSAJE_HISTORIAL_ERROR,
	MENSAJE_HISTORIAL_VACIO,
	TITULO_HISTORIAL,
} from '../../utils/HelpMediciones';

/**
 * Sección Historial reutilizable: tabla con paginación en servidor y sus estados
 * (cargando, error, vacío). Presentacional: no llama a la API; recibe el estado
 * del hook useHistorialMedicion y las columnas { id, label, valor(fila), detalle?(fila) }
 * (detalle: texto secundario opcional bajo el valor).
 */
const HistorialMediciones = ({ id, columnas, historial }) => {
	const classes = medicionesStyles();
	const preinformeClasses = preinformeStyles();
	const {
		estado,
		filas,
		totalElements,
		page,
		size,
		cambiarPagina,
		cambiarTamano,
		reintentar,
	} = historial;
	const tituloId = `${id}-historial-titulo`;

	return (
		<section id={`${id}-historial`} aria-labelledby={tituloId} className={classes.historial}>
			<Typography id={tituloId} variant="subtitle1" component="h4" className={classes.historialTitulo}>
				{TITULO_HISTORIAL}
			</Typography>
			{estado === 'cargando' && <CircularProgress size={24} aria-label="Cargando historial" />}
			{estado === 'error' && (
				<div className={classes.historialEstado}>
					<Typography variant="body2" color="error">{MENSAJE_HISTORIAL_ERROR}</Typography>
					<Button
						type="button"
						variant="outlined"
						color="primary"
						onClick={reintentar}
						className={preinformeClasses.buttonNoTransform}
					>
						Reintentar
					</Button>
				</div>
			)}
			{estado === 'listo' && filas.length === 0 && (
				<Typography variant="body2">{MENSAJE_HISTORIAL_VACIO}</Typography>
			)}
			{estado !== 'error' && filas.length > 0 && (
				<>
					<TableContainer className={classes.historialTablaContenedor}>
						<Table size="small" aria-labelledby={tituloId} aria-busy={estado === 'cargando'}>
							<TableHead>
								<TableRow>
									{columnas.map((c) => (
										<TableCell key={c.id}>{c.label}</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								{filas.map((fila, indice) => (
									// El DTO no trae id y la lista no se reordena ni filtra en cliente
									// (cada página se reemplaza entera): created + índice es estable
									// dentro de la página y no colisiona si dos registros comparten created.
									<TableRow key={`${fila.created}-${indice}`}>
										{columnas.map((c) => (
											<TableCell key={c.id}>
												{c.valor(fila)}
												{c.detalle && (
													<Typography component="span" variant="caption" display="block" color="textSecondary">
														{c.detalle(fila)}
													</Typography>
												)}
											</TableCell>
										))}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						component="div"
						count={totalElements}
						page={page}
						rowsPerPage={size}
						rowsPerPageOptions={HISTORIAL_OPCIONES_TAMANO}
						onPageChange={(e, p) => cambiarPagina(p)}
						onRowsPerPageChange={(e) => cambiarTamano(parseInt(e.target.value, 10))}
						labelRowsPerPage="Filas por página:"
						labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
						backIconButtonText="Página anterior"
						nextIconButtonText="Página siguiente"
						SelectProps={{ native: true, inputProps: { 'aria-label': 'Filas por página' } }}
						classes={{ toolbar: classes.historialPaginacionToolbar }}
					/>
				</>
			)}
		</section>
	);
};

HistorialMediciones.propTypes = {
	id: PropTypes.string.isRequired,
	columnas: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			valor: PropTypes.func.isRequired,
			detalle: PropTypes.func,
		})
	).isRequired,
	historial: PropTypes.shape({
		estado: PropTypes.oneOf(['cargando', 'error', 'listo']).isRequired,
		filas: PropTypes.array.isRequired,
		totalElements: PropTypes.number.isRequired,
		page: PropTypes.number.isRequired,
		size: PropTypes.number.isRequired,
		cambiarPagina: PropTypes.func.isRequired,
		cambiarTamano: PropTypes.func.isRequired,
		reintentar: PropTypes.func.isRequired,
	}).isRequired,
};

export default HistorialMediciones;
